import React from 'react'
import { type Logo, LogoCloud } from "@/components/common/LogoCloud";
import TextProcessor from "@/components/common/TextProcessor";
import Image from "next/image";
import { type Block } from "@/sections/SectionRenderer";
import HeroImage from "@/images/hero_darkmode.png";
import LightHeroImage from "@/images/hero_lightmode.png";
import HeroLight from "@/images/hero_light.png";
import { HeroButtons } from "@/components/common/HeroButtons";

type PrimaryButton = {
  text: string;
  href: string;
};

type SecondaryButton = {
  text: string;
  href: string;
};

interface HeroData extends Block {
  title: string;
  subtitle: string;
  primaryButton: PrimaryButton;
  secondaryButton?: SecondaryButton; // Add this line with optional operator
  logos: Logo[];
}

export function Hero({ data }: { data: HeroData }) {
   const { id, title, subtitle, primaryButton, secondaryButton, logos } = data;

   return (
     <div id={id} className="relative isolate overflow-hidden bg-hero font-mulish text-snowbank z-0">
       {/* Background images */}
       <div className="absolute inset-0 z-[-1]">
         {/* Light-mode effect */}
         <Image
           src={HeroLight}
           alt="Hero light effect"
           width={1200}
           height={800}
           unoptimized
           priority
           className="hidden dark:block absolute top-[-5%] left-[18%] transition-opacity duration-500 ease-in-out opacity-0 dark:opacity-100"
           style={{ mixBlendMode: 'hard-light' }}
         />
         {/* Dark background image */}
         <Image
           unoptimized
           placeholder="blur"
           priority
           quality={100}
           alt="Hero dark background"
           src={HeroImage}
           className="hidden dark:block w-full h-full object-cover 3xl:object-contain object-[center_-10px] md:object-[5%_center] lg:object-[200px_-100px] min-[1441px]:object-[300px_-100px] 2xl:object-[400px_-80px] 3xl:object-[center_-80px]"
         />
         {/* Light background image */}
         <Image
           unoptimized
           placeholder="blur"
           priority
           quality={100}
           alt="Hero light background"
           src={LightHeroImage}
           className="block dark:hidden w-full h-full object-cover 3xl:object-contain object-[center_-10px] md:object-[5%_center] lg:object-[200px_-100px] min-[1441px]:object-[300px_-100px] 2xl:object-[400px_-80px] 3xl:object-[center_-80px]"
         />
       </div>

       {/* Content container */}
       <div className="mx-auto px-6 pt-6 lg:flex lg:px-32 lg:py-40 z-10">
         <div className="flex flex-col mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:pt-8 z-10 text-primary relative">
           <h1
             className="order-1 mt-0 lg:mt-10 text-4xl sm:text-6xl font-light tracking-tight lg:drop-shadow-none
               text-gray-900 drop-shadow-sm dark:text-white dark:drop-shadow-lg transition-colors"
           >
             <TextProcessor className="leading-tight">
               {title}
             </TextProcessor>
           </h1>

           <p
             className="lg:order-2 order-3 self-end mb-12 lg:mb-0 mt-32 lg:mt-6 text-lg leading-8 hidden lg:block
               text-gray-600 font-semibold dark:text-primary dark:font-bold transition-colors"
           >
             {subtitle}
           </p>
           {/* Buttons - now client component */}
           <HeroButtons 
             primaryButton={primaryButton} 
             secondaryButton={secondaryButton}
           />
         </div>
       </div>

       {/* Logo cloud */}
       <LogoCloud 
         className={`${logos.length > 0 ? "" : "hidden lg:block"} relative z-10 pb-24 sm:pb-32`}
         logos={logos} 
       />
     </div>
   );
}
